/**
 * Favorites Edge Client
 * HTTP client for favorites Edge functions
 * NO direct DB access - all operations go through Edge
 */

import { getCachedSession } from '@/contexts/AuthContext';
import { getEdgeFunctionUrl } from '@/lib/config/environment';

export type FavoriteEntityType = 'listing' | 'help_request';

export interface SetFavoriteRequest {
  entityType: FavoriteEntityType;
  entityId: string;
  desiredState: boolean;
}

export interface SetFavoriteResponse {
  entity_type: string;
  entity_id: string;
  is_favorited: boolean;
  updated_at: string;
}

export interface FavoriteListingCard {
  favorited_at: string;
  listing_id: string;
  pet_id: string;
  pet_name: string | null;
  species_key: string;
  species_name: string;
  breed_name: string | null;
  sex: string | null;
  age_months: number | null;
  geo_place_name: string | null;
  primary_media_preview_url: string | null;
}

export interface ListFavoritesResponse {
  items: FavoriteListingCard[];
  hasMore: boolean;
  nextCursor: {
    cursorFavoritedAt: string;
    cursorListingId: string;
  } | null;
}

export interface CheckFavoritesResponse {
  results: Record<string, boolean>;
}

import { createTraceId } from '@/lib/services/logger';

function getAuthHeaders(traceId?: string): HeadersInit {
  const session = getCachedSession();
  if (!session?.access_token) {
    throw new Error('Not authenticated');
  }
  return {
    'Authorization': `Bearer ${session.access_token}`,
    'Content-Type': 'application/json',
    ...(traceId && { 'X-Trace-Id': traceId }),
  };
}

/**
 * Set favorite state (add/remove)
 * Uses desired-state model, NOT toggle
 */
export async function setFavorite(
  entityType: FavoriteEntityType,
  entityId: string,
  desiredState: boolean
): Promise<SetFavoriteResponse> {
  const traceId = createTraceId();
  const headers = getAuthHeaders(traceId);
  
  const response = await fetch(getEdgeFunctionUrl('favorites-set'), {
    method: 'POST',
    headers,
    body: JSON.stringify({
      entityType,
      entityId,
      desiredState,
    } as SetFavoriteRequest),
  });

  if (!response.ok) {
    const errorData = await response.json().catch(() => ({ error: 'Unknown error' }));
    const serverTraceId = errorData.trace_id || traceId;
    console.error(`[favorites-set] Error (trace: ${serverTraceId}):`, errorData);
    throw new Error(errorData.error || `HTTP ${response.status}`);
  }

  return response.json();
}

/**
 * List favorited listings with keyset pagination
 */
export async function listFavoriteListings(
  limit = 20,
  cursorFavoritedAt?: string,
  cursorListingId?: string
): Promise<ListFavoritesResponse> {
  const traceId = createTraceId();
  const headers = getAuthHeaders(traceId);
  
  const params = new URLSearchParams({ limit: String(limit) });
  if (cursorFavoritedAt) params.set('cursorFavoritedAt', cursorFavoritedAt);
  if (cursorListingId) params.set('cursorListingId', cursorListingId);

  const response = await fetch(`${getEdgeFunctionUrl('favorites-listings')}?${params}`, {
    method: 'GET',
    headers,
  });

  if (!response.ok) {
    const errorData = await response.json().catch(() => ({ error: 'Unknown error' }));
    const serverTraceId = errorData.trace_id || traceId;
    console.error(`[favorites-listings] Error (trace: ${serverTraceId}):`, errorData);
    throw new Error(errorData.error || `HTTP ${response.status}`);
  }

  return response.json();
}

/**
 * Check favorite status for multiple entities (batch)
 */
export async function checkFavorites(
  entityType: FavoriteEntityType,
  entityIds: string[]
): Promise<Record<string, boolean>> {
  const traceId = createTraceId();
  const headers = getAuthHeaders(traceId);
  
  const response = await fetch(getEdgeFunctionUrl('favorites-check'), {
    method: 'POST',
    headers,
    body: JSON.stringify({
      entityType,
      entityIds,
    }),
  });

  if (!response.ok) {
    const errorData = await response.json().catch(() => ({ error: 'Unknown error' }));
    const serverTraceId = errorData.trace_id || traceId;
    console.error(`[favorites-check] Error (trace: ${serverTraceId}):`, errorData);
    throw new Error(errorData.error || `HTTP ${response.status}`);
  }

  const data: CheckFavoritesResponse = await response.json();
  return data.results;
}

export const favoritesEdgeClient = {
  setFavorite,
  listFavoriteListings,
  checkFavorites,
};

export default favoritesEdgeClient;
